'use client';

import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {Check, ChevronsUpDown, SearchIcon} from 'lucide-react';

import {cn} from '@/lib/utils';
import {Button} from '@/components/ui/button';
import {Command, CommandEmpty, CommandGroup, CommandItem, CommandList,} from '@/components/ui/command';
import {Popover, PopoverContent, PopoverTrigger,} from '@/components/ui/popover';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {Field, FieldError, FieldLabel} from '@/components/ui/field.tsx';
import type {TLabelValue} from '@/components/form/form.tsx';
import type {UseQueryResult} from '@tanstack/react-query';

type Props = {
  name: string;
  items?: TLabelValue[];
  useQuery?: (params: any) => UseQueryResult<TLabelValue[], any>;
  className?: string;
  label?: string;
  placeholder?: string
  required?: boolean;
  selected?: string;
  orientation?: 'horizontal' | 'vertical';
  queryParams?: any;
  clear?: boolean;
  emptyMessage?: string;
  inputClassName?: string;
};

export function ComboboxField({
  name,
  items,
  useQuery,
  label,
  selected: selectedIn,
  className,
  inputClassName,
  orientation,
  placeholder = '',
  queryParams,
  clear,
  emptyMessage = 'No item found.',
}: Props) {
  const {control, setValue: setFormValue} = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(String(control._formValues[name] || ''));
  const [selected, setSelected] = useState(selectedIn || '');
  const [search, setSearch] = React.useState('');
  const [options, setOptions] = useState(items || []);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {data} = useQuery ? useQuery({q: search, ...queryParams}) : {data: undefined as any};
  useEffect(() => {
    if (data) {
      setOptions(data);
    }
  }, [data]);
  const filteredOptions = useMemo(() => options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  ), [options, search]);
  const watch = useWatch({control, name});
  useEffect(() => {
    const _selected = options.find((o) => o.value === watch);
    const _selectedLabel = _selected ? _selected.label : '';
    if (watch && String(watch) === value) {
      if (selected !== _selectedLabel) {
        setSelected(_selectedLabel);
      }
      return;
    }
    if (!watch && value) {
      setValue('');
      setSelected('');
    } else {
      if (watch && String(watch) !== value) {
        setValue(watch || '');
        setSelected(_selected ? _selected.label : '');
      }
    }
  }, [watch, options]);

  const handleClear = () => {
    setValue('');
    setSelected('');
    setFormValue(name, '', {shouldDirty: true});
    setOpen(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState}) => (
        <div className={cn('field-wrap', orientation, className)}>
          <Field
            data-invalid={fieldState.invalid}
            className={className}
            orientation={orientation}
          >
            {label && <FieldLabel>{label}</FieldLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={cn('min-w-50 justify-between grow', className)}
                >
                  <span className={cn('grow text-left font-normal overflow-hidden', !selected && 'text-muted-foreground')}>
                    {selected ? selected : placeholder}
                  </span>
                  <ChevronsUpDown className="opacity-50"/>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[200px] p-0">
                <Command>
                  <div
                    data-slot="command-input-wrapper"
                    className="flex h-9 items-center gap-2 border-b px-3"
                  >
                    <input
                      data-slot="command-input"
                      className={cn(
                        'placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
                        inputClassName
                      )}
                      placeholder={placeholder}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <SearchIcon className="size-4 shrink-0 opacity-50"/>
                  </div>
                  <CommandList>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                    <CommandGroup>
                      {filteredOptions.map((i, index) => (
                        <CommandItem
                          key={i.value+'-'+index}
                          value={i.value}
                          onSelect={(val) => {
                            const _selected = options.find(o => o.value === val);
                            setValue(val);
                            field.onChange(val);
                            setSelected(_selected ? _selected.label : '');
                            setOpen(false);
                          }}
                        >
                          {i.label}
                          <Check
                            className={cn(
                              'ml-auto',
                              value === i.value ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {value && clear && (
                      <CommandGroup>
                        <CommandItem
                          onSelect={handleClear}
                          className="flex-1 justify-center cursor-pointer"
                        >
                          Clear
                        </CommandItem>
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>
          {fieldState.invalid && <FieldError errors={[fieldState.error]}/>}
        </div>
      )}
    />
  );
}
